import React, { useEffect, useImperativeHandle } from 'react'
import {
  useForm,
  FormProvider,
  SubmitHandler,
  SubmitErrorHandler,
  FieldValues,
  UseFormProps,
  UseFormReturn,
} from 'react-hook-form'

import { FormChangeHandler } from './types'

const Form = <V extends FieldValues>(props: FormProps<V>): JSX.Element => {
  const {
    children,
    onSubmit = () => {},
    onError = () => {},
    onChange,
    className,
    mode = 'onChange',
    formRef,
    ...formProps
  } = props

  const methods = useForm<V>({
    mode,
    ...formProps,
  })

  useEffect(() => {
    if (!onChange) { return }

    const { unsubscribe } = methods.watch((data, field) => onChange(data, field, methods))

    return unsubscribe
  }, [onChange, methods])

  useImperativeHandle(formRef, () => methods)

  return (
    <FormProvider {...methods}>
      <form
        onSubmit={methods.handleSubmit<V>(onSubmit, onError)}
        className={className}
      >
        {children}
      </form>
    </FormProvider>
  )
}

type FormRefType<V extends FieldValues> = React.Ref<UseFormReturn<V>>

type FormProps<V extends FieldValues> = UseFormProps<V, object> & {
  children: React.ReactNode,
  onSubmit?: SubmitHandler<V>,
  onError?: SubmitErrorHandler<V>,
  onChange?: FormChangeHandler<V>,
  className?: string,
  formRef?: FormRefType<V>,
}

export default Form
